import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import {
  Check,
  ClockHistory,
  Icon1CircleFill,
  Icon2CircleFill,
  Icon3CircleFill,
  InfoCircle,
  QuestionCircle,
  Stickies,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import { usePageView } from "../../../../hooks/usePageView";

interface IDomainConnectInfoProps {
  userDomain: string;
  isSubdomainMode: boolean;
  handleBack: () => void;
  handleVerify: () => void;
}

enum COPY_TYPE {
  NS1 = "ns1",
  NS2 = "ns2",
}

export default function DomainConnectInfo({
  userDomain,
  isSubdomainMode = false,
  handleBack,
  handleVerify,
}: IDomainConnectInfoProps) {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const { isMobileView } = usePageView();
  const [copyState, setCopyState] = useState<{ ns1: boolean; ns2: boolean }>({
    ns1: false,
    ns2: false,
  });

  const handleClickCopy = (type: COPY_TYPE) => {
    if (type === COPY_TYPE.NS1) setCopyState({ ...copyState, ns1: true });
    if (type === COPY_TYPE.NS2) setCopyState({ ...copyState, ns2: true });

    setTimeout(() => {
      setCopyState({ ns1: false, ns2: false });
    }, 1000);
  };

  return (
    <div className="text-start">
      <div className="mb-2">
        <span className={`fw-bold${isMobileView ? " fs-4" : " fs-3"}`}>
          {t("projects.Setup & Connect")}
        </span>{" "}
        <span className={`${isMobileView ? " fs-4" : " fs-3"}`}>
          {userDomain}
        </span>
      </div>
      <div className="text-gray mb-3 mb-md-4">
        <span className={`${isMobileView ? " fs-7" : ""}`}>
          {t(
            "projects.Use name servers as easiest way to connect your domain."
          )}
        </span>
      </div>
      <div className="text-gray rounded-2 bg-gray-light d-inline-block p-2 mb-3 mb-md-4 fs-7">
        <ClockHistory className="ms-1 me-2 mb-1" />
        <span>{t("projects.All changes can take to 72 hours.")}</span>
      </div>

      <div className="border border-light rounded-2 p-4 d-flex gap-2 mb-3">
        <div>
          <Icon1CircleFill className="text-primary" />
        </div>
        <div>
          <div
            className={`fw-bold mb-1 mb-md-2${isMobileView ? " fs-6" : " fs-5"}`}
          >
            {t("projects.Login to Your Provider Account")}
          </div>
          <div className={`text-gray${isMobileView ? " fs-7" : ""}`}>
            {t("projects.Login to your account and go to domains page. Find")}{" "}
            <Link
              className="text-primary"
              to="https://me-cluster.com/"
              target={"_blank"}
            >
              me-cluster.com
            </Link>{" "}
            {t("projects.and go to it’s settings page.")}
          </div>
        </div>
      </div>

      <div className="border border-light rounded-2 p-4 d-flex gap-2 mb-3">
        <div>
          <Icon2CircleFill className="text-primary" />
        </div>
        <div>
          <div className={`fw-bold mb-2${isMobileView ? " fs-6" : " fs-5"}`}>
            {isSubdomainMode
              ? t("projects.Update CNAME Record For Domain")
              : t("projects.Update Name Servers")}
          </div>
          <div className={`text-gray mb-3${isMobileView ? " fs-7" : ""}`}>
            {t("projects.updfateNameServersInfo")}
          </div>

          <Col xl={6} className="mb-3">
            <div className="rounded bg-gray-light d-flex justify-content-between align-items-center ps-3">
              {isSubdomainMode ? (
                <div>proxyd.me-team.org</div>
              ) : (
                <div>ns1.me-team.org</div>
              )}
              <Button
                variant="link"
                className={`w-auto ${copyState.ns1 && "pe-none"}`}
                onClick={() => {
                  handleClickCopy(COPY_TYPE.NS1);
                  navigator.clipboard.writeText(
                    isSubdomainMode ? "proxyd.me-team.org" : "ns1.me-team.org"
                  );
                }}
              >
                {!copyState.ns1 ? (
                  <Stickies className="text-primary fs-4" />
                ) : (
                  <Check className="text-primary fs-4" />
                )}
              </Button>
            </div>
          </Col>
          {!isSubdomainMode && (
            <Col xl={6} className="mb-3">
              <div className="rounded bg-gray-light d-flex justify-content-between align-items-center ps-3">
                <div>ns2.me-team.org</div>
                <Button
                  variant="link"
                  className={`w-auto ${copyState.ns2 && "pe-none"}`}
                  onClick={() => {
                    handleClickCopy(COPY_TYPE.NS2);
                    navigator.clipboard.writeText("ns2.me-team.org");
                  }}
                >
                  {!copyState.ns2 ? (
                    <Stickies className="text-primary fs-4" />
                  ) : (
                    <Check className="text-primary fs-4" />
                  )}
                </Button>
              </div>
            </Col>
          )}
          <div className="d-flex align-items-center">
            <InfoCircle className="text-gray fs-7 me-2" />
            <p className="fs-7 text-gray fw-normal m-0">
              {t(
                "projects.If you use a proxy server, you need to disable it when setting up the domain."
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="border border-light rounded-2 p-4 mb-4">
        <div className="d-flex gap-2 mb-4">
          <div>
            <Icon3CircleFill className="text-primary" />
          </div>
          <div>
            <div
              className={`fw-bold mb-1 mb-md-2${isMobileView ? " fs-6" : " fs-5"}`}
            >
              {t("projects.Verify Connection")}
            </div>
            <div className={`text-gray${isMobileView ? " fs-7" : ""}`}>
              {t("projects.verifyInfo")}
            </div>
          </div>
        </div>
        <Row className="justify-content-center gx-3 gx-md-4 gy-3">
          <Col xs={4} md={4}>
            <Button
              variant="outline-dark"
              className="w-100 fw-normal h-100 dark-hover"
              style={{ padding: "12px" }}
              onClick={handleBack}
            >
              {t("support.back")}
            </Button>
          </Col>
          <Col xs={8} md={4}>
            <Button
              variant="primary"
              className="w-100 text-white h-100"
              style={{ padding: "12px" }}
              onClick={handleVerify}
            >
              {t("projects.Verify Connection")}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="d-flex text-gray align-items-center mb-5 fs-7">
        <QuestionCircle className="me-2 fs-6" />
        {t("projects.Need help?")}
        <Link className="text-primary ms-1" to={`${lng}/support`}>
          {t("projects.Contact Us")}
        </Link>
      </div>
    </div>
  );
}
